import { selector, atom } from 'recoil';
import { UserFilters, UserLocation, UserSearch, Properties, CountyState } from './interfaces/interfaces';
import { defaultRichmondLocation, stateMap } from './components/Map/MapUtilities';
import { fetchProperties } from './services/search/properties';
import { formatUrl, userFilterDefaults } from './utilities/stateUtils';
import { countyStateList, stateList } from './services/getCounties';
import MapStyles from './components/Map/MapStyles.json';

// List of States
export const gsStateList = atom<string[]>({
	key: 'gsStateList',
	default: stateList,
});

// List of Counties
export const gsCountiesWithRealtyList = atom<CountyState[]>({
	key: 'gsCountiesWithRealtyList',
	default: countyStateList,
});

export const gsHasInitialized = atom<boolean>({
	key: 'gsHasInitialized',
	default: false
});

// Default Map to Richmond
export const gsUserLocation = atom<UserLocation>({
	key: 'gsUserLocation',
	default: defaultRichmondLocation
});

export const gsUserSearchLocation = atom<UserLocation | null>({
	key: 'gsUserSearchLocation',
	default: null
});

export const gsUserSearchArea = atom<(number[][] | undefined)>({
	key: 'gsUserSearchArea',
	default: undefined
});

export const gsLocationAutoCompleteValue = atom({
	key: 'gsLocationAutoCompleteValue',
	default: ''
});

export const gsUserSearch = atom<UserSearch>({
	key: 'gsUserSearch',
	default: {}
});

/*  recoil does caching on async selectors, i.e. gsProperties, however it requires a primitive key
		this selector creates a string key for the fetchProperties dependencies to enable caching
*/
const gsPropertySearchString = selector<string>({
	key: 'gsPropertiesSearchStringWithPagination',
	get: async ({ get }) => {
		const location = get(gsUserLocation);
		const filters = get(gsFilters);
		const mapScriptLoaded = get(gsMapScriptLoaded);
		const pageSize = get(gsPageSize);
		const pageNumber = get(gsPageNumber);

		return JSON.stringify({ location, filters, mapScriptLoaded, pageSize,  pageNumber });
	}
});

// Active list of properties for cards & map
export const gsProperties = selector<Properties>({
	key: 'gsProperties',
	get: async ({ get }) => {

		const {
			location,
			filters,
			mapScriptLoaded,
			pageSize,
			pageNumber
		}: {
			location: UserLocation,
			filters: UserFilters,
			mapScriptLoaded: boolean,
			pageSize: number,
			pageNumber: number
		} = JSON.parse(get(gsPropertySearchString));


		const propertyListSort: number | null = get(gsPropertyListSort);

		//console.log('%c MAYBE Fetching props with Key: ', 'background: lightgreen; color: white', get(gsPropertySearchString))
		if (mapScriptLoaded && filters.initialized && location.initialized) {
			//console.log('%cFetching props with Location: ', 'background: green; color: white', location)
			return await fetchProperties(location, filters, pageSize, pageNumber, propertyListSort);
		}
		return { total: 0, propertyArray: [] };
	}
});

export const gsCurrentPropertyIds = atom<string[]>({
	key: 'gsCurrentPropertyIds',
	default: []
});


export const gsMapOptions = atom({
	key: 'gsMapOptions',
	default: {
		styles: MapStyles,
		gestureHandling: 'greedy',
		fullscreenControl: false,
		clickableIcons: false,
		zoomControlOptions: {
			position: 8,
		},
		streetViewControl: true,
		streetViewControlOptions: {
			position: 8,
		},
	},
});
// informs UI if Google Maps script has loaded
export const gsMapScriptLoaded = atom({
	key: 'gsMapScriptLoaded',
	default: false,
});

// informs UI of pageSize
export const gsPageSize = atom({
	key: 'gsPageSize',
	default: 100,
});

// informs UI of pageNumber
export const gsPageNumber = atom({
	key: 'gsPageNumber',
	default: 0,
});

// informs UI of current viewport width
export const gsScreenWidth = atom({
	key: 'gsScreenWidth',
	default: 0,
});

// informs UI if viewport width should be considered as mobile device
export const gsIsMobile = atom({
	key: 'gsIsMobile',
	default: false,
});


// Controls visibility of map and list view when user on mobile device (via gsIsMobile state)
export const gsMobileView = atom({
	key: 'gsMobileView',
	default: 1,
});


// Controls mapping of hovered property tile in sidebar to corresponding map marker
export const gsHoveredElement = atom<string[]>({
	key: 'gsHoveredElement',
	default: ['', ''],
});

// Set when user clicks on map marker; should scroll property card into view in sidebar
export const gsClickedMarker = atom({
	key: 'gsClickedMarker',
	default: '',
});


/////// ASYNC LOADING STATES ///////

// True on initial load until <GoogleMapReact> fires prop 'onTilesLoaded'
export const gsMapTilesLoading = atom({
	key: 'gsMapTilesLoading',
	default: true
});

/////// FILTERS && SORTING ///////

/***
 **  PROPERTY CARDS/LIST SORTING
 **/
// Used to sort the list of property cards
export const gsPropertyListSort = atom({
	key: 'gsPropertyListSort',
	default: 0
});


// Used to inform UI if not sorting by 'Newest'
export const gsPropertyListSortFilterApplied = atom({
	key: 'gsPropertyListSortFilterApplied',
	default: false
});

/***
 **  PRICE RANGE FILTERS
 **/

// Informs UI if a min/max price filter is applied.
// Used for displaying active filter count on mobile
export const gsPriceRangeFilterAppliedSelector = selector({
	key: 'gsPriceRangeFilterAppliedSelector',
	get: ({ get }) => {
		const { priceMin, priceMax } = get(gsFilters);
		if (priceMin !== 0 || (priceMax && priceMax >= 0)) {
			return true;
		}
		return false;
	}
});

// Stores state of filters in navbar
export const gsFilters = atom<UserFilters>({
	key: 'gsFilters',
	default: userFilterDefaults
});

export const gsFilterCount = selector<number>({
	key: 'gsFilterCount',
	get: ({ get }) => {
		const filters = get(gsFilters);

		let filterCount = 0;
		if (filters.priceMin !== 0 || filters.priceMax !== -1)
			filterCount++;
		if (filters.lotSizeMin !== 0 || filters.lotSizeMax !== -1)
			filterCount++;
		if (filters.yearBuiltMin !== 0 || filters.yearBuiltMax !== -1)
			filterCount++;
		if (filters.squareFeetMin !== 0 || filters.squareFeetMax !== -1)
			filterCount++;
		if (filters.bathroomsMin !== 0)
			filterCount++;
		if (filters.bedroomsMin !== 0)
			filterCount++;
		if (filters.parkingSpotMin !== 0)
			filterCount++;
		if (filters.maxHoaFee !== -1)
			filterCount++;
		if (filters.hasGarage === true)
			filterCount++;
		if (filters.homeTypes.length > 0)
			filterCount++;
    if (filters.openHouse)
      filterCount++;

		return filterCount;
	}
});

export const gsFiltersActive = selector<boolean>({
	key: 'gsFiltersActive',
	get: ({ get }) => {
		const filters = get(gsFilters);
		if (filters.propertyType !== 'ForSale')
			return true;
		if (filters.priceMin !== 0 || filters.priceMax !== -1)
			return true;
		if (filters.lotSizeMin !== 0 || filters.lotSizeMax !== -1)
			return true;
		if (filters.yearBuiltMin !== 0 || filters.yearBuiltMax !== -1)
			return true;
		if (filters.squareFeetMin !== 0 || filters.squareFeetMax !== -1)
			return true;
		if (filters.bedroomsMin !== 0)
			return true;
		if (filters.bathroomsMin !== 0)
			return true;
		if (filters.parkingSpotMin !== 0)
			return true;
		if (filters.maxHoaFee !== -1)
			return true;
		if (filters.hasGarage === true)
			return true;
		if (filters.homeTypes.length > 0)
			return true;
		if (filters.searchArea !== undefined)
			return true;
    if (filters.openHouse)
      return true;

		return false;
	}
});



export const gsBedsFilterApplied = atom({
	key: 'gsBedsBathsFilterApplied',
	default: false,
});

export const gsBathsFilterApplied = atom({
	key: 'gsBathsFilterApplied',
	default: false,
});

// generates url from dependent parties
export const gsURL = selector({
	key: 'gsURL',
	get: ({ get }) => {
		const location: UserLocation | null = get(gsUserLocation);
		const filters: UserFilters | null = get(gsFilters);
		const sorters: number | null = get(gsPropertyListSort);

		const filtersActive: boolean = get(gsFiltersActive);
		const sortersActive: boolean = get(gsPropertyListSortFilterApplied);

		//console.log('%c Location in Selector: ', 'background: green; color: white', location);
		//console.log('%c Filters in Selector: ', 'background: green; color: white', filters);
		//console.log('%c Sorters in Selector: ', 'background: green; color: white', sorters);

		let urlStateObject: any = {};

		const createLocationUrl = () => {
			if (location && location.boundsElastic) {

				let top = '';
				let left = '';
				let bottom = '';
				let right = '';

				const { zoomLevel, boundsElastic } = location;

				if (boundsElastic.top_left && boundsElastic.bottom_right) {
					top = boundsElastic.top_left.lat.toFixed(4);
					left = boundsElastic.top_left.lon.toFixed(4);
					bottom = boundsElastic.bottom_right.lat.toFixed(4);
					right = boundsElastic.bottom_right.lon.toFixed(4);
				} else if (boundsElastic.top_right && boundsElastic.bottom_left) {
					top = boundsElastic.top_right.lat.toFixed(4);
					right = boundsElastic.top_right.lon.toFixed(4);
					bottom = boundsElastic.bottom_left.lat.toFixed(4);
					left = boundsElastic.bottom_left.lon.toFixed(4);
				} else if (boundsElastic.top && boundsElastic.right && boundsElastic.bottom && boundsElastic.left) {
					top = boundsElastic.top.toFixed(4);
					right = boundsElastic.right.toFixed(4);
					bottom = boundsElastic.bottom.toFixed(4);
					left = boundsElastic.left.toFixed(4);
				}

				if (top !== ''
					&& right !== ''
					&& bottom !== ''
					&& left !== '') {

					urlStateObject = {
						top,
						right,
						bottom,
						left,
						zoomLevel,
						isValid: () => (top && right && bottom && left)

					};
				}
			}
			if (location?.address && location.city && location.state && location.zip)
				return formatUrl('address', [location.address, location.city, location.state, location.zip]);
			else if (location?.zip)
				return formatUrl('postal-code', location.zip);
			else if (location?.city && location?.state)
				return formatUrl('city', [location.city, location.state]);
			else if (location?.county && location?.state)
				return formatUrl('county', [location.county, location.state]);
			else if (location?.state)
				return formatUrl('state', stateMap[location.state.toUpperCase()].toLowerCase());
			else if (urlStateObject.isValid && urlStateObject.isValid())
				return `/geo/${urlStateObject.top},${urlStateObject.left},${urlStateObject.bottom},${urlStateObject.right},${urlStateObject.zoomLevel}`;

			return '';

		};


		const createFilterUrl = () => {
			if (filtersActive) {

				let filterUrlSegment = '/filters/';
				if (filters.priceMax !== -1)
					filterUrlSegment += `,priceMax=${filters.priceMax}`;
				if (filters.priceMin !== 0)
					filterUrlSegment += `,priceMin=${filters.priceMin}`;
				if (filters.propertyType !== 'ForSale')
					filterUrlSegment += `,propertyType=${filters.propertyType}`;
				if (filters.bathroomsMin !== 0)
					filterUrlSegment += `,bathroomsMin=${filters.bathroomsMin}`;
				if (filters.bedroomsMin !== 0)
					filterUrlSegment += `,bedroomsMin=${filters.bedroomsMin}`;
				if (filters.squareFeetMin !== 0)
					filterUrlSegment += `,squareFeetMin=${filters.squareFeetMin}`;
				if (filters.squareFeetMax !== -1)
					filterUrlSegment += `,squareFeetMax=${filters.squareFeetMax}`;
				if (filters.lotSizeMin !== 0)
					filterUrlSegment += `,lotSizeMin=${filters.lotSizeMin}`;
				if (filters.lotSizeMax !== -1)
					filterUrlSegment += `,lotSizeMax=${filters.lotSizeMax}`;
				if (filters.yearBuiltMin !== 0)
					filterUrlSegment += `,yearBuiltMin=${filters.yearBuiltMin}`;
				if (filters.yearBuiltMax !== -1)
					filterUrlSegment += `,yearBuiltMax=${filters.yearBuiltMax}`;
				if (filters.parkingSpotMin !== 0)
					filterUrlSegment += `,parkingSpotMin=${filters.parkingSpotMin}`;
				if (filters.hasGarage !== false)
					filterUrlSegment += `,hasGarage=${filters.hasGarage}`;
				if (filters.homeTypes.length > 0)
					filterUrlSegment += `,homeTypes=${filters.homeTypes.join('-')}`;
				if (filters.maxHoaFee > 0)
					filterUrlSegment += `,maxHoaFee=${filters.maxHoaFee}`;
				if (filters.searchArea !== undefined)
					filterUrlSegment += `,searchArea=${JSON.stringify(filters.searchArea).replace(/,/g, '+')}`;
        if (filters.openHouse)
          filterUrlSegment += `,openHouse=${filters.openHouse}`;
        
				return filterUrlSegment.replace('/,', '/');
			}
			return '';
		};

		const createSortUrl = () => {
			if (sorters && sortersActive) {

				let sortUrlSegment = '/sort/';
				if (sorters === 0)
					sortUrlSegment += 'newest';
				else if (sorters === 1)
					sortUrlSegment += 'pricedescending';
				else if (sorters === 2)
					sortUrlSegment += 'priceascending';

				return sortUrlSegment;
			}
			return '';
		};

		const locationSegment = createLocationUrl();
		const filterSegment = createFilterUrl();
		const sortSegment = createSortUrl();

		const url = '/location' + locationSegment + filterSegment + sortSegment;
		if (url === '/location')
			return '';
		return url;
	}
});
